import { fxConvert } from "@/lib/fx";

/** The fixed host-referral reward, denominated in USD. */
export const REFERRAL_REWARD_USD = 50;

/**
 * Explicit "$50" fallback used when the active display currency has no
 * FX rate. Formatted as USD so we never re-stamp 50 with a foreign
 * symbol (e.g. "€50").
 */
export const REFERRAL_REWARD_USD_FALLBACK_LABEL = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(REFERRAL_REWARD_USD);

/**
 * Format the fixed $50 host-referral reward in the visitor's active
 * display currency, rounded DOWN to the nearest whole unit so we never
 * overstate the payout. Falls back to an explicitly-USD "$50" when the
 * FX table doesn't know the currency.
 *
 * Shared by the public refer-a-host landing page and the referral
 * dashboard so the reward figure always agrees between the two for the
 * same selected currency. `format` is the active `CurrencyContext`
 * formatter (`useCurrency().format`).
 */
export function formatReferralReward(
  currency: string,
  format: (amount: number) => string,
): string {
  if (currency === "USD") return format(REFERRAL_REWARD_USD);
  const converted = fxConvert(REFERRAL_REWARD_USD, "USD", currency);
  if (converted == null || !Number.isFinite(converted)) {
    return REFERRAL_REWARD_USD_FALLBACK_LABEL;
  }
  const floored = Math.floor(converted);
  if (floored <= 0) return REFERRAL_REWARD_USD_FALLBACK_LABEL;
  return format(floored);
}
